import type PdfDocument from 'jspdf';

import {
  REPORT_PAGE_HEIGHT,
  REPORT_PAGE_WIDTH,
  REPORT_THEME,
} from '../../../../PageConfigProvider/constants';
import { drawCoverPage, type PdfCoverParams } from './pdf-cover';

export type { PdfCoverParams };

const DEFAULT_IMAGE_QUALITY = 0.85;

const pxToPt = (px: number): number => (px * 72) / 96;

const resolvePageBackground = (): string =>
  getComputedStyle(document.body)
    .getPropertyValue('--ui-chakra-bg-primary')
    .trim() || '#ffffff';

const yieldToBrowser = (): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, 0));

const captureMapCanvases = (element: HTMLElement): Map<string, string> => {
  const canvasDataMap = new Map<string, string>();
  const mapCanvases = element.querySelectorAll<HTMLCanvasElement>(
    '.maplibregl-canvas, canvas[class*="maplibre"]'
  );

  mapCanvases.forEach((canvas, index) => {
    try {
      const dataUrl = canvas.toDataURL('image/png');
      const key = `map-canvas-${index}`;
      canvasDataMap.set(key, dataUrl);
    } catch (err) {
      console.warn('Failed to capture map canvas:', err);
    }
  });

  return canvasDataMap;
};

const replaceMapCanvasesWithImages = (
  clonedDoc: Document,
  canvasDataMap: Map<string, string>
) => {
  const mapCanvases = clonedDoc.querySelectorAll<HTMLCanvasElement>(
    '.maplibregl-canvas, canvas[class*="maplibre"]'
  );

  mapCanvases.forEach((canvas, index) => {
    const key = `map-canvas-${index}`;
    const dataUrl = canvasDataMap.get(key);

    if (dataUrl) {
      const img = clonedDoc.createElement('img');
      img.src = dataUrl;

      const computedStyle =
        clonedDoc.defaultView?.getComputedStyle(canvas) ??
        window.getComputedStyle(canvas);
      img.style.width = computedStyle.width;
      img.style.height = computedStyle.height;
      img.style.position = computedStyle.position;
      img.style.top = computedStyle.top;
      img.style.left = computedStyle.left;
      img.style.display = 'block';

      if (canvas.parentNode) {
        canvas.parentNode.replaceChild(img, canvas);
      }
    }
  });
};

const fixSvgScalingForExport = async (clonedDoc: Document) => {
  const imgs = Array.from(clonedDoc.querySelectorAll('img'));

  for (const img of imgs) {
    const isSvg =
      img.src.includes('.svg') || img.src.startsWith('data:image/svg+xml');
    if (!isSvg) continue;

    // Ensure the image is loaded so naturalWidth/naturalHeight are available
    await new Promise<void>((resolve) => {
      if (img.complete && img.naturalWidth > 0) {
        resolve();
      } else {
        img.onload = () => resolve();
        img.onerror = () => resolve();
      }
    });

    const computedStyle =
      clonedDoc.defaultView?.getComputedStyle(img) ??
      window.getComputedStyle(img);
    const width = parseFloat(computedStyle.width);
    const height = parseFloat(computedStyle.height);

    if (width > 0 && height > 0) {
      try {
        const canvas = document.createElement('canvas');
        const scale = 4;
        canvas.width = width * scale;
        canvas.height = height * scale;

        const ctx = canvas.getContext('2d');
        if (ctx) {
          const objectFit = computedStyle.objectFit;

          if (
            objectFit === 'cover' &&
            img.naturalWidth > 0 &&
            img.naturalHeight > 0
          ) {
            // Simulate object-fit: cover by cropping the source image
            const naturalW = img.naturalWidth;
            const naturalH = img.naturalHeight;
            const containerRatio = width / height;
            const imageRatio = naturalW / naturalH;

            // Parse objectPosition — computed style returns "X% Y%" (e.g. "0% 100%")
            const pos = computedStyle.objectPosition || '50% 50%';
            const parts = pos.split(/\s+/);
            const xPos = parseFloat(parts[0] || '50') / 100 || 0.5;
            const yPos = parseFloat(parts[1] || parts[0] || '50') / 100 || 0.5;

            let sx = 0;
            let sy = 0;
            let sw = naturalW;
            let sh = naturalH;

            if (imageRatio < containerRatio) {
              // Image is taller — crop top/bottom
              sh = naturalW / containerRatio;
              sy = (naturalH - sh) * yPos;
            } else {
              // Image is wider — crop left/right
              sw = naturalH * containerRatio;
              sx = (naturalW - sw) * xPos;
            }

            ctx.drawImage(
              img,
              sx,
              sy,
              sw,
              sh,
              0,
              0,
              canvas.width,
              canvas.height
            );
          } else {
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          }

          img.src = canvas.toDataURL('image/png');

          img.style.width = `${width}px`;
          img.style.height = `${height}px`;
          img.style.objectFit = 'fill';
          img.style.objectPosition = '';
          img.setAttribute('width', width.toString());
          img.setAttribute('height', height.toString());
        }
      } catch (err) {
        const rawSrc = img.src ?? '';
        const src =
          rawSrc.length > 200
            ? `${rawSrc.slice(0, 200)}…`
            : rawSrc || '(empty)';
        console.warn(
          'PDF export: failed to rasterize an SVG image, skipping.',
          src,
          err
        );
      }
    }
  }
};

class PdfGenerator {
  private options: PdfExportOptions;

  constructor(options: PdfExportOptions) {
    this.options = options;
  }

  private createPageCanvas(
    fullCanvas: HTMLCanvasElement,
    offsetY: number,
    pageHeight: number,
    canvasWidth: number,
    background: string
  ): HTMLCanvasElement {
    const pageCanvas = document.createElement('canvas');
    const ctx = pageCanvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');

    pageCanvas.width = canvasWidth;
    pageCanvas.height = pageHeight;

    ctx.fillStyle = background;
    ctx.fillRect(0, 0, canvasWidth, pageHeight);

    ctx.drawImage(
      fullCanvas,
      0,
      offsetY,
      canvasWidth,
      pageHeight,
      0,
      0,
      canvasWidth,
      pageHeight
    );

    return pageCanvas;
  }

  // biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
  public async export(): Promise<void | { blob: Blob; fileName: string }> {
    const {
      element,
      fileNameBase,
      gridRows,
      pageHeightPx = REPORT_PAGE_HEIGHT,
      pageWidthPx: providedPageWidthPx,
      canvasScale = 3,
      imageQuality = DEFAULT_IMAGE_QUALITY,
      onProgress,
      returnBlob = false,
    } = this.options;

    let pageWidthPx = providedPageWidthPx;
    if (pageWidthPx === undefined) {
      pageWidthPx = REPORT_PAGE_WIDTH;
    }

    const pageWidthPt = pxToPt(pageWidthPx);

    // Both libraries are large and only needed once an export runs.
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import('html2canvas-pro'),
      import('jspdf'),
    ]);

    await yieldToBrowser();

    const mapCanvasData = captureMapCanvases(element);
    const pageBackground = resolvePageBackground();

    let fullCanvas = await html2canvas(element, {
      scale: canvasScale,
      useCORS: true,
      allowTaint: true,
      backgroundColor: pageBackground,
      height: element.scrollHeight || pageHeightPx,
      onclone: async (clonedDoc) => {
        replaceMapCanvasesWithImages(clonedDoc, mapCanvasData);
        await fixSvgScalingForExport(clonedDoc);

        Array.from(
          clonedDoc?.querySelectorAll('svg text, svg tspan') || []
        ).forEach((e) => {
          const existingStyle = e.getAttribute('style') || '';
          e.setAttribute(
            'style',
            existingStyle + '; font-size: 10px !important'
          );
        });
      },
    });

    let canvasWidth = fullCanvas.width;
    let canvasHeight = fullCanvas.height;

    const computedStyle = window.getComputedStyle(element);
    const paddingLeft = parseFloat(computedStyle.paddingLeft) || 0;
    const paddingRight = parseFloat(computedStyle.paddingRight) || 0;
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0;
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0;

    if (
      paddingLeft > 0 ||
      paddingRight > 0 ||
      paddingTop > 0 ||
      paddingBottom > 0
    ) {
      const scale = canvasScale;
      const cropLeft =
        Math.max(0, paddingLeft - REPORT_THEME.margins.pdf) * scale;
      const cropTop =
        Math.max(0, paddingTop - REPORT_THEME.margins.pdf) * scale;
      const cropRight =
        Math.max(0, paddingRight - REPORT_THEME.margins.pdf) * scale;
      const cropBottom =
        Math.max(0, paddingBottom - REPORT_THEME.margins.pdf) * scale;

      const contentLeft = Math.round(cropLeft);
      const contentTop = Math.round(cropTop);
      const contentWidth = Math.round(canvasWidth - cropLeft - cropRight);
      const contentHeight = Math.round(canvasHeight - cropTop - cropBottom);

      const finalWidth = contentWidth;
      const finalHeight = contentHeight;

      const contentCanvas = document.createElement('canvas');
      contentCanvas.width = finalWidth;
      contentCanvas.height = finalHeight;
      const contentCtx = contentCanvas.getContext('2d');

      if (contentCtx) {
        contentCtx.fillStyle = pageBackground;
        contentCtx.fillRect(0, 0, finalWidth, finalHeight);

        contentCtx.drawImage(
          fullCanvas,
          contentLeft,
          contentTop,
          contentWidth,
          contentHeight,
          0,
          0,
          finalWidth,
          finalHeight
        );

        canvasWidth = finalWidth;
        canvasHeight = finalHeight;
        fullCanvas = contentCanvas;
      }
    }

    const desiredPageHeightCanvasPx = Math.round(pageHeightPx * canvasScale);
    const totalPages =
      gridRows ?? Math.ceil(fullCanvas.height / desiredPageHeightCanvasPx);

    const cover = this.options.cover;
    const skipFirstCanvasPage = !!cover;
    const fullPageHeightPt =
      (desiredPageHeightCanvasPx / fullCanvas.width) * pageWidthPt;

    let pdf: PdfDocument | undefined;

    if (cover) {
      pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidthPt, fullPageHeightPt],
      });
      try {
        await drawCoverPage(pdf, pageWidthPt, fullPageHeightPt, cover);
      } catch (err) {
        console.warn(
          '[pdf-export] cover-page rendering failed, falling back to captured cover',
          err
        );
      }
      if (onProgress) onProgress(1, totalPages);
    }

    const startPageIndex = skipFirstCanvasPage ? 1 : 0;

    for (let pageIndex = startPageIndex; pageIndex < totalPages; pageIndex++) {
      await yieldToBrowser();
      const offsetY = pageIndex * desiredPageHeightCanvasPx;
      const remainingHeight = fullCanvas.height - offsetY;

      if (remainingHeight < 20 * canvasScale) break;

      const pageHeight = Math.min(desiredPageHeightCanvasPx, remainingHeight);
      if (pageHeight <= 0) break;

      const pageCanvas = this.createPageCanvas(
        fullCanvas,
        offsetY,
        pageHeight,
        fullCanvas.width,
        pageBackground
      );
      const pageImgData = pageCanvas.toDataURL('image/jpeg', imageQuality);

      const imgHeightPt = (pageHeight / fullCanvas.width) * pageWidthPt;

      if (!pdf) {
        pdf = new jsPDF({
          orientation: 'portrait',
          unit: 'pt',
          format: [pageWidthPt, imgHeightPt],
        });
      } else {
        pdf.addPage([pageWidthPt, imgHeightPt]);
      }

      pdf.addImage(pageImgData, 'JPEG', 0, 0, pageWidthPt, imgHeightPt);

      if (onProgress) onProgress(pageIndex + 1, totalPages);
    }

    if (returnBlob && pdf) {
      const blob = pdf.output('blob');
      const fileName = buildPdfFileName(fileNameBase);
      return { blob, fileName };
    } else {
      pdf?.save(buildPdfFileName(fileNameBase));
    }
  }
}

const buildPdfFileName = (baseName: string): string => {
  const trimmed = baseName.trim();
  if (!trimmed) return 'dashboard-export.pdf';
  return trimmed.toLowerCase().endsWith('.pdf') ? trimmed : `${trimmed}.pdf`;
};

export interface PdfExportResult {
  getFile: () => Promise<void>;
  getBlob: () => Promise<{ blob: Blob; fileName: string } | null>;
}

export interface PdfExportOptions {
  element: HTMLElement;
  fileNameBase: string;
  gridRows?: number;
  pageHeightPx?: number;
  pageWidthPx?: number;
  canvasScale?: number;
  imageQuality?: number;
  onProgress?: (current: number, total: number) => void;
  returnBlob?: boolean;
  /**
   * If provided, the first page of the PDF is generated directly via jsPDF
   * (vector + image + text) instead of taken from the captured canvas. The
   * cover slice of the canvas is skipped. Use this when the live render of
   * the cover doesn't survive the html2canvas trip reliably (complex SVG,
   * `object-fit`, viewport-dependent sizing, etc.).
   */
  cover?: PdfCoverParams;
}

const exportElementToPdf = async (
  options: PdfExportOptions
  // biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
): Promise<void | { blob: Blob; fileName: string }> => {
  const generator = new PdfGenerator(options);
  return generator.export();
};

export const generatePdfBlob = async (
  options: Omit<PdfExportOptions, 'returnBlob'>
): Promise<{ blob: Blob; fileName: string }> => {
  const result = await exportElementToPdf({ ...options, returnBlob: true });
  if (!result) {
    throw new Error('Failed to generate PDF blob');
  }
  return result;
};

export const exportElementToPdfWithResult = (
  options: Omit<PdfExportOptions, 'returnBlob'>
): PdfExportResult => ({
  async getFile() {
    await exportElementToPdf({ ...options, returnBlob: false });
  },
  async getBlob() {
    const result = await exportElementToPdf({ ...options, returnBlob: true });
    return result || null;
  },
});
